"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import ScanQRCode from "./ScanQRCode";

export default function RSVP({
  heading,
  subtitle,
  wishesHeading,
  wishesSubtitle,
  countdownDate,
  initialWishes = [],
  virtualBoothImage,
  backgroundImage,
  __isMobile = false,
}) {
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };
  const titleClass = __isMobile ? "text-3xl" : "text-3xl md:text-4xl";
  const stepTitleClass = __isMobile ? "text-3xl" : "text-3xl md:text-4xl";
  const resolvedBackgroundImage =
    (typeof backgroundImage === "string" && backgroundImage.trim() !== ""
      ? backgroundImage
      : typeof virtualBoothImage === "string" && virtualBoothImage.trim() !== ""
        ? virtualBoothImage
        : "");
  const hasBackgroundImage =
    resolvedBackgroundImage !== "";

  const [rsvps, setRsvps] = useState(initialWishes);
  const [formData, setFormData] = useState({
    name: "",
    attendance: "",
    message: "",
  });
  const [step, setStep] = useState(1);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNext = () => {
    if (step === 1 && formData.name.trim() === "") {
      alert("Please enter your name");
      return;
    }
    if (step === 2 && formData.attendance === "") {
      alert("Please select your attendance status");
      return;
    }
    setStep(step + 1);
  };

  const handlePrevious = () => {
    setStep(step - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      formData.name.trim() === "" ||
      formData.attendance === "" ||
      formData.message.trim() === ""
    ) {
      alert("Please complete all required fields");
      return;
    }

    setRsvps([formData, ...rsvps]);
    setFormData({ name: "", attendance: "", message: "" });
    setStep(1);
  };

  const handleOpenVB = () => {
    const ref = "?invitation=Jason%20%26%20Isabella";
    window.open(`https://virtualbooth.dreamwedding.id/${ref}`, "_blank");
  };

  return (
    <div className="relative px-4 py-8 text-white max-w-xl mx-auto overflow-hidden">
      {hasBackgroundImage && (
        <>
          <Image
            src={resolvedBackgroundImage}
            alt="RSVP background"
            fill
            className="absolute inset-0 -z-10 object-cover"
            sizes="(min-width: 768px) 420px, 100vw"
          />
          <div className="absolute inset-0 z-0 bg-black/55" />
        </>
      )}
      <div className="relative z-10">
      {/* RSVP Form */}
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeInUp}
        className="mb-10"
      >
        {heading && (
          <h2 className={`text-center font-cormorant font-semibold ${titleClass}`}>
            {heading}
          </h2>
        )}
        {subtitle && <p className="text-center text-xs my-3">{subtitle}</p>}

        <div className="flex justify-center space-x-4 mb-4">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`w-8 h-8 rounded-full flex items-center justify-center border transition-all ${step === s
                ? "bg-white text-black font-semibold"
                : "border-white text-white"
                }`}
            >
              {s}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {step === 1 && (
            <input
              type="text"
              name="name"
              placeholder="name"
              autoComplete="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 bg-white text-black border placeholder-black/50"
            />
          )}

          {step === 2 && (
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() =>
                  setFormData({ ...formData, attendance: "hadir" })
                }
                className={`w-1/2 text-center px-4 py-2 border ${formData.attendance === "hadir"
                  ? "bg-white text-black font-semibold"
                  : "bg-white/10 text-white"
                  }`}
              >
                EXCITED TO ATTEND
              </button>
              <button
                type="button"
                onClick={() =>
                  setFormData({ ...formData, attendance: "tidak_hadir" })
                }
                className={`w-1/2 text-center px-4 py-2 border ${formData.attendance === "tidak_hadir"
                  ? "bg-white text-black font-semibold"
                  : "bg-white/10 text-white"
                  }`}
              >
                UNABLE TO ATTEND
              </button>
            </div>
          )}

          {step === 3 && (
            <textarea
              name="message"
              placeholder="WISHES"
              value={formData.message}
              onChange={handleChange}
              required
              className="w-full border px-4 py-2 bg-white/10 text-white placeholder-white"
            />
          )}

          <div className="flex justify-between pt-2">
            {step > 1 && (
              <button
                type="button"
                onClick={handlePrevious}
                className="bg-white/10 border border-white px-6 py-2 rounded-full text-white"
              >
                PREVIOUS
              </button>
            )}

            {step < 3 ? (
              <button
                type="button"
                onClick={handleNext}
                className="bg-white px-6 py-2 rounded-full text-black"
                disabled={
                  (step === 1 && formData.name.trim() === "") ||
                  (step === 2 && formData.attendance === "")
                }
              >
                NEXT
              </button>
            ) : (
              <button
                type="submit"
                className="bg-white px-6 py-2 rounded-full text-black"
                disabled={formData.message.trim() === ""}
              >
                SEND
              </button>
            )}
          </div>
        </form>
      </motion.div>

      {/* Wishes List */}
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeInUp}
        className="text-white"
      >
        {wishesHeading && (
          <h2 className={`font-cormorant font-semibold text-center mb-2 ${stepTitleClass}`}>
            {wishesHeading}
          </h2>
        )}
        {wishesSubtitle && (
          <p className="text-center text-xs mb-4">{wishesSubtitle}</p>
        )}

        <div className="max-h-75 overflow-y-auto pr-2">
          <div className="grid grid-cols-1 gap-4">
            {rsvps.map((wish, index) => (
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
                key={index}
                className="bg-white/20 backdrop-blur-md rounded-xl p-4 border border-white/30 shadow-lg"
              >
                <p className="italic font-cormorant">&ldquo;{wish.message}&rdquo;</p>
                <p className="text-sm text-right mt-2 font-semibold">- {wish.name}</p>
                <p className="text-xs text-right text-white/70">
                  {wish.attendance === "hadir"
                    ? "Will attend"
                    : "Unable to attend"}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      <div className="my-4">
        <ScanQRCode countdown={countdownDate} __isMobile={__isMobile} />
      </div>

      {/* 🔹 Virtual Booth pakai props */}
      <motion.div
        id="virtualbooth"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeInUp}
        className="text-white scroll-mt-24"
      >
        <h2 className={`font-cormorant uppecase font-semibold text-center mt-4 ${stepTitleClass}`}>
          Virtual Booth
        </h2>
        <p className="text-center font-caudex text-xs mb-4">
          Tinggalkan pesan, foto, atau video untuk mempelai tercinta.
        </p>
        <div className="relative">
          <div className="w-full h- rounded-lg bg-white/25 border border-white">
            {typeof virtualBoothImage === "string" && virtualBoothImage.trim() !== "" && (
              <Image
                className="w-full h-full object-cover rounded-lg"
                src={virtualBoothImage}
                width={1920}
                height={1080}
                alt="VirtualBooth"
              />
            )}
          </div>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 m-2">
            <button
              onClick={handleOpenVB}
              className="cursor-pointer bg-white px-6 py-2 font-cormorant font-semibold text-black uppercase"
            >
              <p className="w-max">Buat Ucapan</p>
            </button>
          </div>
        </div>
      </motion.div>
      </div>
    </div>
  );
}
